import {
  Modal,
  BlockStack,
  Banner,
  Text,
  InlineStack,
  Button,
  Spinner,
} from "@shopify/ui-extensions-react/customer-account";
import { FileUploadInstructions } from "./FileUploadInstructions";
import { ItemUploadSection } from "./ItemUploadSection";
import { SeatUploadBanner } from "./SeatUploadBanner";
import { UploadModalProps } from "../types";
import { useUploadModal } from "../hooks/useUploadModal";

export const UploadModal = ({ onClose, orderId }: UploadModalProps) => {
  const {
    orderDetails,
    orderLoading,
    orderError,
    seatRequirements,
    hasSeatProducts,
    files,
    isUploading,
    fileProgress,
    error,
    uploadResults,
    requiredPhotosUploaded,
    handleFileInput,
    removeFile,
    handleClose,
    handleCancel,
    handleUploadClick,
    modalTitle,
    loadingText,
    noPhotosRequiredTitle,
    noPhotosRequiredMessage,
    allPhotosUploadedTitle,
    allPhotosUploadedMessage,
    successMessage,
    cancelButtonText,
    submitButtonText,
    uploadingButtonText,
    maxFiles,
  } = useUploadModal({
    onClose,
    orderId,
  });

  return (
    <Modal id="upload-modal" onClose={handleClose} title={modalTitle} padding size="max">
      <BlockStack spacing="loose">
        {orderLoading && (
          <BlockStack spacing="base" inlineAlignment="center">
            <Spinner size="small" />
            <Text appearance="subdued">{loadingText}</Text>
          </BlockStack>
        )}

        {orderError && (
          <Banner status="critical">
            <Text>{orderError}</Text>
          </Banner>
        )}

        {!hasSeatProducts && orderDetails && !orderLoading && (
          <Banner status="info">
            <Text emphasis="bold">{noPhotosRequiredTitle}</Text>
            <Text>{noPhotosRequiredMessage}</Text>
          </Banner>
        )}

        {hasSeatProducts && (
          <>
            {seatRequirements && (
              <SeatUploadBanner seatRequirements={seatRequirements} />
            )}

            <FileUploadInstructions
              seatRequirements={seatRequirements}
              maxFiles={maxFiles}
            />

            {requiredPhotosUploaded && (
              <Banner status="success">
                <Text emphasis="bold">{allPhotosUploadedTitle}</Text>
                <Text>{allPhotosUploadedMessage}</Text>
              </Banner>
            )}

            {seatRequirements?.seatRequirements.map((req, index) => (
              <ItemUploadSection
                key={req.itemId}
                requirement={req}
                files={files}
                fileProgress={fileProgress}
                onFileSelect={handleFileInput}
                onRemove={removeFile}
                isUploading={isUploading}
                defaultExpanded={index === 0}
              />
            ))}
          </>
        )}

        {hasSeatProducts && error && (
          <Banner status="critical">
            <Text>{error}</Text>
          </Banner>
        )}

        {hasSeatProducts && uploadResults.length > 0 && (
          <Banner status="success">
            <Text>{successMessage}</Text>
          </Banner>
        )}

        <BlockStack spacing="base">
          <InlineStack spacing="base" inlineAlignment="end">
            <Button
              kind="secondary"
              onPress={handleCancel}
              disabled={isUploading}
            >
              {cancelButtonText}
            </Button>
            {hasSeatProducts && (
              <Button
                kind="primary"
                onPress={handleUploadClick}
                disabled={isUploading || !requiredPhotosUploaded}
                loading={isUploading}
              >
                {isUploading ? uploadingButtonText : submitButtonText}
              </Button>
            )}
          </InlineStack>
        </BlockStack>
      </BlockStack>
    </Modal>
  );
};
